import { errorToast } from '@hooks/toast.hook';
import { AnyFieldMeta, Updater } from '@tanstack/react-form';

/**
 * Safely extracts an error message from an error entry (string or object)
 * @param errorEntry - The error entry which can be a string or object
 * @returns The error message string or null if not found
 */
const extractErrorMessage = (errorEntry: unknown): string | null => {
  if (typeof errorEntry === 'string') {
    return errorEntry;
  }
  if (errorEntry && typeof errorEntry === 'object' && 'message' in errorEntry) {
    const message = (errorEntry as { message: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }
  return null;
};

/**
 * Safely extracts error message from an array of unknown error entries
 * @param errors - Array of unknown error entries
 * @returns The first valid error message or null if none found
 */
const extractErrorFromArray = (errors: unknown): string | null => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return null;
  }

  for (const errorEntry of errors) {
    const message = extractErrorMessage(errorEntry);
    if (message) {
      return message;
    }
  }

  return null;
};

/**
 * Extracts the first meaningful error message from form validation errors
 * @param formErrors - The form errors object from formApi.getAllErrors()
 * @returns A user-friendly error message
 */
export const extractFormErrorMessage = (formErrors: Record<string, any>): string => {
  const defaultMessage = 'Please fill out all required fields correctly.';

  // Early return if formErrors is not a valid object
  if (!formErrors || typeof formErrors !== 'object') {
    return defaultMessage;
  }

  // Check form-level errors first
  const formLevelErrors = formErrors.form?.errors;
  if (formLevelErrors) {
    const formErrorMessage = extractErrorFromArray(formLevelErrors);
    if (formErrorMessage) {
      return formErrorMessage;
    }

    // Handle nested form errors structure
    if (Array.isArray(formLevelErrors) && formLevelErrors.length > 0) {
      const firstFormError = formLevelErrors[0];
      if (firstFormError && typeof firstFormError === 'object') {
        const formErrorKeys = Object.keys(firstFormError);
        for (const errorKey of formErrorKeys) {
          const errorArray = (firstFormError as Record<string, unknown>)[errorKey];
          const nestedMessage = extractErrorFromArray(errorArray);
          if (nestedMessage) {
            return nestedMessage;
          }
        }
      }
    }
  }

  // If no form errors, check field-level errors
  const fieldErrors = formErrors.fields;
  if (!fieldErrors || typeof fieldErrors !== 'object') {
    return defaultMessage;
  }

  const fieldKeys = Object.keys(fieldErrors);
  if (fieldKeys.length === 0) {
    return defaultMessage;
  }

  // Check each field for errors
  for (const fieldKey of fieldKeys) {
    const fieldError = fieldErrors[fieldKey];
    if (!fieldError || typeof fieldError !== 'object') {
      continue;
    }

    // Check direct errors array
    const directErrors = (fieldError as Record<string, unknown>).errors;
    const directMessage = extractErrorFromArray(directErrors);
    if (directMessage) {
      return directMessage;
    }

    // Check errorMap.onSubmit
    const errorMap = (fieldError as Record<string, unknown>).errorMap;
    if (errorMap && typeof errorMap === 'object') {
      const onSubmitErrors = (errorMap as Record<string, unknown>).onSubmit;
      const submitMessage = extractErrorFromArray(onSubmitErrors);
      if (submitMessage) {
        return submitMessage;
      }
    }
  }

  return defaultMessage;
};

/**
 * Creates an onSubmitInvalid handler that extracts and displays meaningful error messages
 * @returns An onSubmitInvalid handler function
 */
export const createFormInvalidHandler = <TFormData = unknown>() => {
  return (props: {
    formApi: { getAllErrors: () => Record<string, any> };
    value: TFormData;
    meta: unknown;
  }) => {
    if (!props || !props.formApi || typeof props.formApi.getAllErrors !== 'function') {
      errorToast({ description: 'Please fill out all required fields correctly.' });
      return;
    }

    const formErrors = props.formApi.getAllErrors();
    const errorMessage = extractFormErrorMessage(formErrors);

    errorToast({ description: errorMessage });
  };
};

/**
 * Build a `(fieldName, text) => void` applier from a per-field writer map, so a
 * form section wires both the inline suggestion accept and the chat workspace
 * accept through one declarative field->write mapping. Unknown fields no-op.
 */
export const createSuggestionTextApplier = <TFieldName extends string>(
  writers: Partial<Record<TFieldName, (text: string) => void>>,
) => {
  return (fieldName: TFieldName, text: string): void => {
    writers[fieldName]?.(text);
  };
};

export const resetFieldMeta: Updater<AnyFieldMeta> = (meta) => ({
  ...meta,
  isTouched: false,
  isDirty: false,
  errors: [],
  errorMap: {},
  errorSourceMap: {},
  isValid: true,
});

export const buildIssuePath = (pathSegments: Array<PropertyKey>): string | undefined => {
  return pathSegments.reduce<string>((accumulator, segment, index) => {
    if (typeof segment === 'number') {
      return `${accumulator}[${segment}]`;
    }

    if (typeof segment !== 'string') {
      return accumulator;
    }

    if (index === 0) {
      return segment;
    }

    return `${accumulator}.${segment}`;
  }, '');
};

export const createStepValidationError = (
  issues: Array<{
    message: string;
    path: Array<PropertyKey>;
  }>,
): { fields: Record<string, string> } => {
  const fields: Record<string, string> = {};

  for (const issue of issues) {
    const issuePath = buildIssuePath(issue.path);

    if (!issuePath || fields[issuePath]) {
      continue;
    }

    fields[issuePath] = issue.message;
  }

  return {
    fields,
  };
};
